import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { FieldNameBadge } from './FieldNameBadge';

const PinIcon = ({ className = '' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M7.535,17.9,1.707,23.707.293,22.293l5.828-5.809Zm6.3,2.765a7.478,7.478,0,0,0,1.942-7.146l-.312-1.276,3.62-3.64.57.571a2.578,2.578,0,0,0,3.293.346,2.5,2.5,0,0,0,.318-3.805L18.344.788A2.581,2.581,0,0,0,15.051.442a2.5,2.5,0,0,0-.319,3.806l.647.646-3.621,3.64L10.49,8.223a7.479,7.479,0,0,0-7.154,1.941l-.353.354,10.5,10.5Z"/>
  </svg>
);

export interface StatItem {
  id: string;
  label: React.ReactNode;
  value: number | string;
  previousValue?: number | string;
  changePercent?: number;
  onClick?: () => void;
  selected?: boolean;
  dimmed?: boolean; // Show at reduced opacity (for filtered-out items)
  labelClass?: string; // Custom class for the label text
  colorClass?: string; // Custom text color class for the value
  description?: string; // Optional description shown below label
  bgClass?: string; // Custom background class for the cell
  selectedBgClass?: string; // Custom background class when selected
  selectedBorderColor?: string; // Custom left border color when selected (CSS color value)
}

export type TimePeriod = 'today' | 'tomorrow' | 'this_week';

interface StatsGridProps {
  title?: string;
  stats: StatItem[];
  className?: string;
  timePeriod?: TimePeriod;
  onTimePeriodChange?: (period: TimePeriod) => void;
  loading?: boolean;
  columns?: number;
  rows?: number;
  pinned?: boolean;
  onPinnedChange?: (pinned: boolean) => void;
  size?: 'lg' | 'md' | 'sm'; // Font size variant
  bgColor?: string; // Custom background color class
}

const LoadingSpinner = () => (
  <svg className="animate-spin h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const ArrowUpIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="mr-0.5 -ml-1 size-5 shrink-0 self-center text-green-500 dark:text-green-400">
    <path d="M10 17a.75.75 0 0 1-.75-.75V5.612L5.29 9.77a.75.75 0 0 1-1.08-1.04l5.25-5.5a.75.75 0 0 1 1.08 0l5.25 5.5a.75.75 0 1 1-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0 1 10 17Z" clipRule="evenodd" fillRule="evenodd" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="mr-0.5 -ml-1 size-5 shrink-0 self-center text-red-500 dark:text-red-400">
    <path d="M10 3a.75.75 0 0 1 .75.75v10.638l3.96-4.158a.75.75 0 1 1 1.08 1.04l-5.25 5.5a.75.75 0 0 1-1.08 0l-5.25-5.5a.75.75 0 1 1 1.08-1.04l3.96 4.158V3.75A.75.75 0 0 1 10 3Z" clipRule="evenodd" fillRule="evenodd" />
  </svg>
);

const ThumbsUpStarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5 ml-1 text-green-600 dark:text-green-500">
    <path d="m8.391,4.034l1.119.911-.442,1.329c-.138.438.017.917.385,1.193.189.141.414.215.649.215.215,0,.423-.063.603-.183l1.305-.874,1.33.867c.385.25.888.229,1.252-.05.364-.281.512-.763.364-1.207l-.455-1.296,1.114-.908c.347-.292.476-.774.32-1.2s-.564-.712-1.018-.712h-1.398l-.503-1.412c-.156-.423-.564-.707-1.015-.707s-.859.284-1.018.712l-.501,1.406h-1.398c-.453,0-.861.285-1.017.708-.157.424-.03.907.324,1.207Zm.693-.916h1.75c.211,0,.4-.133.471-.332l.62-1.738h.15l.62,1.738c.071.199.26.332.471.332h1.75l.06.142-1.38,1.125c-.163.133-.226.354-.156.553l.571,1.628-.127.088-1.606-1.046c-.168-.11-.384-.107-.551.003l-1.579,1.057c-.018.012-.035.014-.047.014l-.082-.101.549-1.649c.065-.197.002-.415-.159-.545l-1.387-1.128.062-.14Zm14.851,2.604c-.156-.426-.564-.712-1.018-.712h-1.398l-.503-1.412c-.156-.423-.564-.707-1.015-.707s-.859.284-1.018.712l-.501,1.406h-1.398c-.453,0-.862.286-1.016.708-.157.425-.031.907.323,1.207l1.119.911-.442,1.329c-.138.438.017.917.384,1.192.188.142.413.216.65.216.215,0,.423-.063.603-.183l1.305-.874,1.33.867c.384.25.888.229,1.252-.05.364-.281.512-.763.364-1.207l-.455-1.296,1.114-.908c.347-.292.476-.774.32-1.2Zm-2.338,1.554c-.163.133-.226.354-.156.553l.571,1.628-.127.088-1.606-1.046c-.168-.109-.385-.107-.551.003l-1.579,1.057c-.018.012-.035.014-.047.014l-.082-.101.549-1.649c.065-.197.002-.415-.159-.545l-1.387-1.128.062-.14h1.75c.211,0,.4-.133.471-.332l.62-1.738h.15l.62,1.738c.071.199.26.332.471.332h1.75l.06.142-1.38,1.125Zm-16.258,3.107c.385.25.888.229,1.252-.05.364-.281.512-.763.364-1.207l-.455-1.296,1.114-.908c.347-.292.476-.774.32-1.2-.156-.426-.564-.712-1.018-.712h-1.398l-.503-1.412c-.156-.423-.564-.707-1.015-.707s-.859.284-1.018.712l-.501,1.406h-1.398c-.453,0-.862.286-1.016.708-.157.425-.031.907.323,1.207l1.119.911-.442,1.329c-.138.438.017.917.384,1.192.188.142.413.216.65.216.215,0,.423-.063.603-.183l1.305-.874,1.33.867Zm-1.612-1.881l-1.579,1.057c-.018.012-.035.014-.047.014l-.082-.101.549-1.649c.065-.197.002-.415-.159-.545l-1.387-1.128.062-.14h1.75c.211,0,.4-.133.471-.332l.62-1.738h.15l.62,1.738c.071.199.26.332.471.332h1.75l.06.142-1.38,1.125c-.163.133-.226.354-.156.553l.571,1.628-.127.088-1.606-1.046c-.167-.109-.384-.107-.551.003Zm13.273,5.498h-4.183l.587-3.33q.023-.508,0-.533c-.121-.444-.402-.794-.791-.985-.394-.192-.848-.2-1.281-.02-.254.105-.502.367-.679.712l-2.196,4.155h-1.456c-1.654,0-3,1.346-3,3v4c0,1.654,1.346,3,3,3h8.379c1.9,0,3.55-1.353,3.923-3.216l.639-3.196c.177-.885-.05-1.792-.622-2.49-.572-.698-1.417-1.098-2.32-1.098Zm-11.999,7v-4c0-1.103.897-2,2-2h1v8h-1c-1.103,0-2-.897-2-2Zm13.96-3.608l-.639,3.196c-.28,1.397-1.517,2.412-2.942,2.412h-6.379v-8.161c.141-.091.259-.218.34-.372l2.2-4.161c.094-.184.182-.251.177-.251.163-.068.324-.069.455-.005.1.049.179.135.247.291l-.586,3.484c-.052.292.027.589.218.818.191.227.47.357.766.357h4.183c.602,0,1.165.267,1.546.732.382.465.533,1.07.415,1.66Z"/>
  </svg>
);

const StatsGrid: React.FC<StatsGridProps> = ({ title, stats, className = '', timePeriod, onTimePeriodChange, loading = false, columns = 5, rows = 2, pinned = false, onPinnedChange, size = 'md', bgColor }) => {
  const totalCells = columns * rows;
  const { showFieldNames, highlightedFieldId } = useDisplayPreferences();

  // Generate grid classes based on columns
  const getGridColsClass = () => {
    switch (columns) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-2';
      case 3: return 'grid-cols-3';
      case 4: return 'grid-cols-4';
      case 5: return 'grid-cols-5';
      case 6: return 'grid-cols-6';
      case 7: return 'grid-cols-7';
      case 8: return 'grid-cols-8';
      default: return 'grid-cols-5';
    }
  };

  // Get size classes based on size prop
  const getSizeClasses = () => {
    switch (size) {
      case 'lg': return { label: 'text-base', value: 'text-4xl', padding: 'px-4 py-3' };
      case 'sm': return { label: 'text-xs', value: 'text-lg', padding: 'px-2 py-1.5' };
      default: return { label: 'text-sm', value: 'text-2xl', padding: 'px-3 py-2' };
    }
  };

  const sizeClasses = getSizeClasses();

  return (
    <div className={`${className} relative`}>
      {title && !onTimePeriodChange && (
        <h3 className="text-base font-semibold text-heading">{title}</h3>
      )}
      {onTimePeriodChange && timePeriod && (
        <div className="relative inline-block">
          <select
            value={timePeriod}
            onChange={(e) => onTimePeriodChange(e.target.value as TimePeriod)}
            className="appearance-none text-base font-semibold text-heading bg-transparent pr-6 cursor-pointer focus:outline-none"
          >
            <option value="today">Today</option>
            <option value="tomorrow">Tomorrow</option>
            <option value="this_week">This Week</option>
          </select>
          <ChevronDownIcon className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
        </div>
      )}
      {onPinnedChange && (
        <button
          onClick={() => onPinnedChange(!pinned)}
          className={`absolute top-0 right-0 p-1.5 transition-colors z-10 ${
            pinned
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
          }`}
          title={pinned ? 'Unpin stats grid' : 'Pin stats grid'}
        >
          <PinIcon className={`h-4 w-4 ${pinned ? '-scale-x-100' : ''}`} />
        </button>
      )}
      <dl className={`${(title || onTimePeriodChange) ? 'mt-3' : ''} grid ${getGridColsClass()} overflow-hidden rounded-lg ${bgColor || 'bg-panel'} shadow-sm border border-gray-200 dark:border-gray-700 divide-x divide-y divide-gray-200 dark:divide-gray-700`}
      >
        {stats.map((stat) => {
          const isHighlighted = highlightedFieldId === stat.id;
          return (
          <div
            key={stat.id}
            data-field-name={stat.id}
            className={`${sizeClasses.padding} ${stat.selected ? (stat.selectedBgClass || 'bg-blue-100 dark:bg-blue-900/40') : (stat.bgClass || '')} ${stat.dimmed ? 'opacity-50' : ''} ${stat.onClick ? 'cursor-pointer hover:brightness-95 dark:hover:brightness-110 transition-all' : ''} ${
              isHighlighted
                ? 'ring-4 ring-amber-500 dark:ring-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.8)] animate-[pulse_1s_ease-in-out_3] relative'
                : 'relative'
            }`}
            style={stat.selected && stat.selectedBorderColor ? { boxShadow: `inset 0 -3px 0 0 ${stat.selectedBorderColor}` } : undefined}
            onClick={stat.onClick}
          >
            {showFieldNames && (
              <FieldNameBadge
                fieldName={stat.id}
                isHighlighted={isHighlighted}
                variant="corner"
              />
            )}
            <div className={`flex items-center justify-between ${size === 'sm' ? 'gap-2' : ''}`}>
              <div className={size === 'sm' ? 'min-w-0' : undefined}>
                <dt className={`${sizeClasses.label} font-bold text-gray-600 dark:text-gray-400 ${size === 'sm' ? 'whitespace-normal' : 'whitespace-nowrap'} ${stat.labelClass || ''}`}>{stat.label}</dt>
                {stat.description && (
                  <div className="text-[0.625rem] text-gray-500 dark:text-gray-500 opacity-75 whitespace-nowrap">{stat.description}</div>
                )}
              </div>
              <dd className={`flex items-center shrink-0 ${sizeClasses.value} font-semibold ${stat.colorClass || (stat.value === 0 || stat.value === '0' ? 'text-green-700 dark:text-green-500' : 'text-red-600 dark:text-red-400')}`}>
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  stat.value
                )}
              </dd>
            </div>
          </div>
          );
        })}
        {/* Fill remaining cells based on columns x rows */}
        {stats.length < totalCells && Array.from({ length: totalCells - stats.length }).map((_, i) => (
          <div key={`empty-${i}`} className="px-3 py-2">&nbsp;</div>
        ))}
      </dl>
    </div>
  );
};

export default StatsGrid;
